'use client';

import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

/**
 * Hjälpfunktion för att kontrollera om Firebase Crashlytics är tillgängligt.
 * Crashlytics stöds endast på nativa enheter (iOS och Android).
 */
export function ärCrashlyticsTillgängligt(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Initierar Firebase Crashlytics på nativa enheter.
 * Aktiverar automatisk insamling och kontrollerar om appen kraschade vid föregående körning.
 */
export async function initieraCrashlytics(): Promise<void> {
  if (!ärCrashlyticsTillgängligt()) {
    return;
  }

  try {
    await FirebaseCrashlytics.setEnabled({ enabled: true });

    const { crashed } = await FirebaseCrashlytics.didCrashOnPreviousExecution();
    if (crashed) {
      console.warn('[Crashlytics] Appen kraschade vid föregående körning. Oskickade rapporter skickas.');
      await FirebaseCrashlytics.sendUnsentReports();
    }
    await FirebaseCrashlytics.log({ message: 'Crashlytics initierat i Flinky' });
  } catch (fel) {
    console.error('[Crashlytics] Misslyckades att initiera Crashlytics:', fel);
  }
}

/**
 * Sätter användaridentitet i Crashlytics för att koppla krascher till en specifik användare.
 */
export async function sättCrashlyticsAnvändare(användarId: string | null): Promise<void> {
  if (!ärCrashlyticsTillgängligt() || !användarId) {
    return;
  }

  try {
    await FirebaseCrashlytics.setUserId({ userId: användarId });
  } catch (fel) {
    console.error('[Crashlytics] Kunde inte sätta användar-ID:', fel);
  }
}

/**
 * Sätter en anpassad nyckel/värde i Crashlytics för ytterligare felsökningskontext.
 */
export async function sättCrashlyticsNyckel(
  nyckel: string,
  värde: string | number | boolean
): Promise<void> {
  if (!ärCrashlyticsTillgängligt()) {
    return;
  }

  try {
    let typ: 'string' | 'int' | 'double' | 'boolean' = 'string';
    if (typeof värde === 'boolean') {
      typ = 'boolean';
    } else if (typeof värde === 'number') {
      typ = Number.isInteger(värde) ? 'int' : 'double';
    }

    await FirebaseCrashlytics.setCustomKey({
      key: nyckel,
      value: värde,
      type: typ,
    });
  } catch (fel) {
    console.error('[Crashlytics] Kunde inte sätta nyckel:', fel);
  }
}

/**
 * Lägger till ett loggmeddelande i Crashlytics tidslinje (breadcrumbs).
 */
export async function loggaTillCrashlytics(meddelande: string): Promise<void> {
  if (!ärCrashlyticsTillgängligt()) {
    return;
  }

  try {
    await FirebaseCrashlytics.log({ message: meddelande });
  } catch (fel) {
    console.error('[Crashlytics] Kunde inte logga meddelande:', fel);
  }
}

/**
 * Rapporterar ett icke-fatalt JavaScript-fel till Crashlytics.
 */
export async function rapporteraTillCrashlytics(
  fel: Error,
  metadata?: { orgId?: string | null; sökväg?: string | null }
): Promise<void> {
  if (!ärCrashlyticsTillgängligt()) {
    return;
  }

  try {
    if (metadata?.orgId) {
      await sättCrashlyticsNyckel('organisationId', metadata.orgId);
    }
    if (metadata?.sökväg) {
      await sättCrashlyticsNyckel('klientSökväg', metadata.sökväg);
    }

    const felmeddelande = `${fel.name || 'Error'}: ${fel.message || String(fel)}`;
    await FirebaseCrashlytics.recordException({
      message: felmeddelande,
    });
  } catch (err) {
    // Svälj fel för att undvika följdfel i felhanteraren
    console.error('[Crashlytics] Kunde inte rapportera exception:', err);
  }
}

/**
 * Tvingar en krasch för att verifiera att Crashlytics fungerar korrekt i testmiljöer.
 * VARNING: Detta kommer omedelbart att krascha den nativa appen.
 */
export async function tvingaKraschTest(meddelande = 'Testkrasch från Flinky DevPanel'): Promise<void> {
  if (!ärCrashlyticsTillgängligt()) {
    throw new Error('Testkrasch kan endast utföras på nativa mobila enheter (iOS/Android).');
  }

  await FirebaseCrashlytics.crash({ message: meddelande });
}
